import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertTriangle, ChevronRight, Loader2, Upload } from 'lucide-react'
import { apiService } from '@/services/apiService'
import { useIncognito } from '@/hooks/use-incognito'
import {
  PARALLEL_CHECK_SIZE,
  parseCSV,
  parseIDs,
  SummaryCard,
  FailureBreakdown,
} from './bulkSystemsShared'

type Step = 'import' | 'confirm' | 'executing' | 'done'

interface Props {
  poolID: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function BulkRemoveSystemsDialog({ poolID, open, onClose, onSuccess }: Props) {
  const incognito = useIncognito()
  const [step, setStep] = useState<Step>('import')
  const [inputText, setInputText] = useState('')
  const [parsedIDs, setParsedIDs] = useState<string[]>([])
  const [csvData, setCsvData] = useState<{ headers: string[]; rows: string[][] } | null>(null)
  const [confirmText, setConfirmText] = useState('')
  const [progress, setProgress] = useState(0)
  const [successes, setSuccesses] = useState<string[]>([])
  const [failures, setFailures] = useState<Array<{ systemID: string; reason: string }>>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cancelledRef = useRef(false)

  const handleTextChange = (text: string) => {
    setInputText(text)
    setParsedIDs(parseIDs(text))
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = (ev.target?.result as string) ?? ''
      const parsed = parseCSV(text)
      if (parsed) {
        setCsvData(parsed)
      } else {
        setInputText(text)
        setParsedIDs(parseIDs(text))
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const startExecuting = async () => {
    cancelledRef.current = false
    setStep('executing')
    setProgress(0)

    const batchSuccesses: string[] = []
    const batchFailures: Array<{ systemID: string; reason: string }> = []

    for (let i = 0; i < parsedIDs.length; i += PARALLEL_CHECK_SIZE) {
      if (cancelledRef.current) return
      const chunk = parsedIDs.slice(i, i + PARALLEL_CHECK_SIZE)
      await Promise.all(
        chunk.map(async (systemID) => {
          const resp = await apiService.systems.remove(poolID, systemID)
          if (resp.success) {
            batchSuccesses.push(systemID)
          } else {
            // A 404 on DELETE /pools/{poolID}/systems/{systemID} means the system
            // isn't a member of this pool — surface that instead of "Resource not found".
            const reason =
              resp.errorCode === 'NOT_FOUND'
                ? 'System is not in this pool'
                : resp.error || 'Failed to remove system'
            batchFailures.push({ systemID, reason })
          }
        })
      )
      setProgress(i + chunk.length)
    }

    setSuccesses(batchSuccesses)
    setFailures(batchFailures)
    setStep('done')
  }

  const reset = () => {
    cancelledRef.current = true
    setStep('import')
    setInputText('')
    setParsedIDs([])
    setCsvData(null)
    setConfirmText('')
    setProgress(0)
    setSuccesses([])
    setFailures([])
  }

  const handleClose = () => {
    if (step === 'done' && successes.length > 0) onSuccess()
    reset()
    onClose()
  }

  const isExecuting = step === 'executing'

  const description = (() => {
    switch (step) {
      case 'import':
        return 'Upload a CSV or paste system IDs to remove them from this pool.'
      case 'confirm':
        return 'Confirm before removing — this action cannot be undone.'
      case 'executing':
        return 'Removing systems — please wait.'
      case 'done':
        return 'All operations complete.'
    }
  })()

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !isExecuting && handleClose()}>
      <DialogContent className="max-w-2xl flex flex-col max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Remove Systems from Pool</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 min-h-0 py-1">
          {step === 'import' && (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt"
                className="hidden"
                onChange={handleFileUpload}
              />
              {csvData ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium">Which column contains the System IDs?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {csvData.headers.map((header, colIdx) => {
                      const samples = csvData.rows
                        .map((row) => row[colIdx]?.trim() ?? '')
                        .filter(Boolean)
                        .slice(0, 3)
                      return (
                        <button
                          key={colIdx}
                          className="rounded-md border p-3 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                          onClick={() => {
                            const values = csvData.rows.map((row) => row[colIdx]?.trim() ?? '').join('\n')
                            setParsedIDs(parseIDs(values))
                            setCsvData(null)
                          }}
                        >
                          <p className="text-sm font-semibold truncate">{header}</p>
                          {samples.map((s, i) => (
                            <p key={i} className="text-xs font-mono text-muted-foreground truncate mt-0.5">
                              {s}
                            </p>
                          ))}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-2"
                    onClick={() => {
                      setCsvData(null)
                      setParsedIDs([])
                      fileInputRef.current?.click()
                    }}
                  >
                    ↑ Upload a different file
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      Upload CSV
                    </Button>
                    <span className="text-sm text-muted-foreground">or paste system IDs below</span>
                  </div>
                  <textarea
                    className="w-full h-40 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                    placeholder={`Paste system IDs here, one per line:\n550e8400-e29b-41d4-a716-446655440000\n123e4567-e89b-12d3-a456-426614174000`}
                    value={inputText}
                    onChange={(e) => handleTextChange(e.target.value)}
                  />
                  {(inputText || parsedIDs.length > 0) && (
                    <p className="text-sm text-muted-foreground">
                      {parsedIDs.length > 0 ? (
                        <span className="text-foreground font-medium">
                          {parsedIDs.length} unique system ID{parsedIDs.length !== 1 ? 's' : ''} found
                        </span>
                      ) : (
                        'No valid system IDs found'
                      )}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {step === 'confirm' && (
            <div className="space-y-4 py-2">
              <p className="text-sm flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 text-destructive mt-0.5" />
                <span>
                  This will remove{' '}
                  <strong>
                    {parsedIDs.length} system{parsedIDs.length !== 1 ? 's' : ''}
                  </strong>{' '}
                  from this pool. This action cannot be undone.
                </span>
              </p>
              <div className="space-y-2">
                <p className="text-sm">
                  Type <strong>DELETE</strong> to confirm:
                </p>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type DELETE"
                  autoFocus
                />
              </div>
            </div>
          )}

          {(step === 'executing' || step === 'done') && (
            <div className="space-y-4 py-4">
              {step === 'executing' && (
                <>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-150"
                      style={{ width: `${parsedIDs.length > 0 ? (progress / parsedIDs.length) * 100 : 0}%` }}
                    />
                  </div>
                  <p className="text-sm text-center text-muted-foreground">
                    Removing {progress} of {parsedIDs.length}…
                  </p>
                </>
              )}
              {step === 'done' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <SummaryCard
                      label="Removed"
                      value={successes.length}
                      colorClass="text-green-600"
                      copyIds={!incognito.isIncognito ? successes : undefined}
                    />
                    <SummaryCard
                      label="Failed"
                      value={failures.length}
                      colorClass={failures.length > 0 ? 'text-destructive' : 'text-muted-foreground'}
                      copyIds={!incognito.isIncognito ? failures.map((f) => f.systemID) : undefined}
                    />
                  </div>
                  <FailureBreakdown failures={failures} isIncognito={incognito.isIncognito} />
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-2">
          {step === 'import' && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirmText('')
                  setStep('confirm')
                }}
                disabled={parsedIDs.length === 0}
              >
                Delete Systems
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <Button variant="outline" onClick={() => setStep('import')}>
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={startExecuting}
                disabled={confirmText !== 'DELETE'}
              >
                Delete Systems ({parsedIDs.length})
              </Button>
            </>
          )}
          {step === 'executing' && (
            <Button variant="outline" disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Removing…
            </Button>
          )}
          {step === 'done' && <Button onClick={handleClose}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
